import { ActionFunctionArgs } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import {
  getRegistrationOptions,
  registerPasskey,
} from '@/portal/api/Passkeys.ts';
import { verifySession } from '@/portal/api/PortalInvitations.ts';
import RegistrationPage from '@/portal/routes/registration/Page.tsx';
import { ApiError } from '@/common/types/errors';

export interface RegistrationLoaderData {
  email: string;
}

export async function loader(): Promise<RegistrationLoaderData> {
  // Verify with the backend that the user has a valid verified session
  try {
    const data = await verifySession();
    return {
      email: data.email,
    };
  } catch (error) {
    console.error('Session verification failed:', error);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new Response('Invalid invitation link', {
      status: 401,
    });
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get('email') as string;
  const firstName = formData.get('firstName') as string;
  const lastName = formData.get('lastName') as string;

  try {
    const optionsJSON = await getRegistrationOptions({
      email,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });

    // Kicks off passkey registration via a challenge provided to us from the options endpoint call above
    const credential = await startRegistration({ optionsJSON });

    await registerPasskey({
      credential,
      email,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });

    return {
      success: true,
      flash: {
        message: 'Registration successful!',
        type: 'success',
      },
    };
  } catch (error) {
    // If it's our API error, try to get the specific error message
    if (error instanceof ApiError) {
      return {
        success: false,
        flash: {
          message: error.message,
          type: 'danger',
        },
      };
    }

    return {
      success: false,
      flash: {
        message:
          error instanceof Error ? error.message : 'An unknown error occurred',
        type: 'danger',
      },
    };
  }
}

export default function RegistrationRoute() {
  return <RegistrationPage />;
}
